/**
 * WorkspaceQuickStats
 *
 * Key-value table in the right column: Total documents, Jurisdictions,
 * Coverage from, Last ingested, Created by — matches the mockup exactly.
 */

import React from 'react';
import type { QuickStats } from '@/types/workspaceDetail';

interface WorkspaceQuickStatsProps {
  stats: QuickStats;
  loading: boolean;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatMonthYear(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}

interface Row {
  label: string;
  value: React.ReactNode;
}

export const WorkspaceQuickStats: React.FC<WorkspaceQuickStatsProps> = ({
  stats,
  loading,
}) => {
  const rows: Row[] = [
    {
      label: 'Total documents',
      value: loading ? '—' : stats.total_docs.toLocaleString(),
    },
    {
      label: 'Jurisdictions',
      value: loading
        ? '—'
        : stats.jurisdiction_count === 1
          ? '1 place'
          : `${stats.jurisdiction_count} places`,
    },
    {
      label: 'Coverage from',
      value: loading ? '—' : formatMonthYear(stats.coverage_from),
    },
    {
      label: 'Coverage to',
      value: loading ? '—' : formatMonthYear(stats.coverage_to),
    },
    {
      label: `Dated documents${loading ? '' : ` (of ${stats.total_docs.toLocaleString()})`}`,
      value: loading ? '—' : stats.dated_docs.toLocaleString(),
    },
    {
      label: 'Last ingested',
      value: loading ? '—' : formatDate(stats.last_ingested_at),
    },
    {
      label: 'Created by',
      value: loading ? '—' : (stats.created_by ?? 'System Admin'),
    },
  ];

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <p className="mb-4 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
        Quick Stats
      </p>
      <dl className="space-y-2.5">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-baseline justify-between gap-3"
          >
            <dt className="text-sm text-gray-500">{row.label}</dt>
            <dd className="text-sm font-semibold text-gray-900 text-right">
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
};
